/**
 * HeaderCard — the "Command Center" hero for a single Property Workspace.
 *
 * Big Deal Score ring (V3 signature), engine-colored stage chip, KPI strip
 * with sparklines, activity chip. Wraps existing property + deal-score
 * history data — no new API calls beyond what was already fetched.
 */
import React, { useEffect, useState } from "react";
import { MapPin, User as UserIcon, Clock } from "lucide-react";
import { api } from "@/lib/api";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";
import Sparkline from "@/pages/v3/Sparkline";
import StrategyPickerPill from "@/components/v3/StrategyPickerPill";

const STAGE_STYLE = {
  lead:           { color: "#64748B", label: "Lead" },
  qualifying:     { color: "#3B82F6", label: "Qualifying" },
  under_contract: { color: "#8B5CF6", label: "Under contract" },
  marketing:      { color: "#F59E0B", label: "Marketing" },
  pending_close:  { color: "#EA580C", label: "Pending close" },
  closed:         { color: "#10B981", label: "Closed" },
  dead:           { color: "#EF4444", label: "Dead" },
};

function fmtAddress(a) {
  if (!a) return "—";
  const line1 = [a.street, a.unit && `#${a.unit}`].filter(Boolean).join(" ");
  const line2 = [a.city, a.state, a.zip_code].filter(Boolean).join(", ");
  return `${line1}, ${line2}`;
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86_400)}d ago`;
}

// Compute the real KPIs for the header from actual property data.
//
// Waterfall (matches `deal_math.full_report_metrics` on the backend, so the
// header, the PDF cover, and the Wholesale strategy card always agree):
//   1. Operator-entered `estimated_arv` / `estimated_repairs` wins
//   2. AI estimate (`ai_arv_mid` / `ai_repair_estimate`) fills the gap
//   3. Any leftover missing input → return `null` so the tile renders "—"
//      instead of pretending it has data.
//
// MAO formula: classic 70% rule = ARV × 0.70 − repairs. Matches the Strategy
// Picker Pill's `flip_70` calculator (both frontend + backend agree) so the
// header MAO number and the pill's Cash Flip number never disagree.
// Wholesaler-with-assignment-fee is a separate figure shown on the
// Wholesale strategy card explicitly, not conflated with generic "MAO".
//
// Historically this function was `seededKpis()` — a UUID-hash placeholder
// that fabricated 5-figure numbers (ARV $250K-$550K etc.) on every deal.
// That's what caused "the ARV and MAO are different again" reports even
// after the PDF-side waterfall was fixed — the frontend was still lying.
function realKpis(p) {
  const arv = Number(
    p?.estimated_arv ?? p?.ai_arv_mid ?? p?.arv ?? 0,
  ) || null;
  const repairs = Number(
    p?.estimated_repairs ?? p?.ai_repair_estimate ?? p?.repair_estimate ?? 0,
  ) || null;

  // Only compute MAO/margin/ROI if we actually have ARV. Otherwise show "—".
  let mao = null;
  let margin = null;
  let roi = null;
  if (arv) {
    mao = Math.max(0, arv * 0.70 - (repairs || 0));
    margin = mao > 0 ? arv - mao - (repairs || 0) : null;
    roi = mao > 0 && margin != null ? Math.round((margin / mao) * 100) : null;
  }
  return { arv, mao, repairs, margin, roi };
}

function fmtMoneyOrDash(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return fmtMoney(n);
}

function fmtMoney(n) {
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

export default function HeaderCard({ property }) {
  const dealScore = property.deal_score ?? property.current_deal_score ?? 0;
  const stage = STAGE_STYLE[property.stage] || STAGE_STYLE.lead;
  const localKpis = realKpis(property);

  // Golden endpoint — the ONE source of truth for numbers. Overrides
  // whatever we could compute locally so the header, the PDF cover, and
  // the Wholesale strategy card literally can't disagree. Fallback to
  // local waterfall when the endpoint is unreachable (offline/slow).
  const [metrics, setMetrics] = useState(null);
  // Bumps whenever the Strategy Picker persists a new strategy — forces
  // /metrics refetch so the KPI tiles reflect the newly-active MAO.
  const [metricsRev, setMetricsRev] = useState(0);
  useEffect(() => {
    if (!property?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/v3/properties/${property.id}/metrics`);
        if (!cancelled) setMetrics(data);
      } catch { /* silent — local waterfall renders instead */ }
    })();
    return () => { cancelled = true; };
  }, [property?.id,
      property?.estimated_arv,
      property?.estimated_repairs,
      property?.ai_arv_mid,
      property?.ai_repair_estimate,
      metricsRev]);

  // Pick canonical values first, fall back to local realKpis waterfall.
  // Active-strategy MAO / margin / ROI overlay the flip_70 defaults so
  // the KPI tiles reflect whichever exit lens is pinned via the pill.
  const active = metrics?.active_strategy;
  const kpis = metrics ? {
    arv:     metrics.inputs?.arv,
    repairs: metrics.inputs?.repairs,
    mao:     (active?.mao ?? metrics.flip_70?.mao),
    margin:  (active?.margin ?? metrics.margin),
    roi:     (active?.roi ?? metrics.roi),
  } : localKpis;

  // Load deal-score history for the sparkline. Silent on failure.
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (!property?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/v3/properties/${property.id}/deal-score/history`);
        if (cancelled) return;
        const items = [...(data.items || [])].reverse();
        setHistory(items);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [property?.id, property?.deal_score_updated_at]);

  const scoreSeries = history.length > 1
    ? history.map((h) => h.score ?? h.value ?? 0)
    : null;

  return (
    <section
      data-testid="workspace-header"
      className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm"
    >
      {/* Branded textures + engine-colored radial glow */}
      <div className="absolute inset-0 v3-hero-texture pointer-events-none" />
      <div className="absolute inset-0 v3-hero-noise pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none"
           style={{
             backgroundImage:
               `radial-gradient(ellipse at 0% 0%, ${stage.color}12, transparent 55%), radial-gradient(ellipse at 100% 0%, ${stage.color}0A, transparent 55%)`,
           }} />
      {/* Top color strip */}
      <div className="h-1 w-full" style={{ backgroundColor: stage.color }} />

      <div className="relative p-6 md:p-8">
        <div className="flex flex-wrap items-start gap-6">
          {/* Left — identity */}
          <div className="min-w-0 flex-1">
            <div
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-black px-2.5 py-1 rounded-full ring-1 ring-slate-300 bg-white/70 backdrop-blur-sm text-slate-700 shadow-sm"
              data-testid="workspace-war-room-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Deal War Room
              <span className="text-slate-400 font-semibold normal-case tracking-normal">· live</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <MapPin className="w-3 h-3" />
              Property
            </div>
            <h1
              data-testid="workspace-address"
              className="mt-1.5 text-[24px] md:text-[30px] font-bold text-slate-900 tracking-tight leading-tight"
              title={fmtAddress(property.address)}
            >
              {fmtAddress(property.address)}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span data-testid="workspace-stage-pill"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded-full ring-1"
                    style={{
                      backgroundColor: `${stage.color}15`,
                      color: stage.color,
                      borderColor: `${stage.color}40`,
                    }}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stage.color }} />
                {stage.label}
              </span>
              {property.lead_source && (
                <span className="text-[11px] text-slate-500">via {property.lead_source}</span>
              )}
              <span data-testid="workspace-assignee"
                    className="inline-flex items-center gap-1 text-[11px] text-slate-600 font-medium bg-slate-100/70 rounded-full px-2 py-0.5">
                <UserIcon className="w-3 h-3 text-slate-400" />
                {property.assigned_user_id ? "You" : "Unassigned"}
              </span>
              <span data-testid="workspace-last-activity"
                    className="inline-flex items-center gap-1 text-[11px] text-slate-600 font-medium bg-slate-100/70 rounded-full px-2 py-0.5">
                <Clock className="w-3 h-3 text-slate-400" />
                {relTime(property.last_activity_at)}
              </span>
              <StrategyPickerPill
                propertyId={property.id}
                dealType={property.deal_type || "house"}
                onChange={() => setMetricsRev((v) => v + 1)}
              />
            </div>

            {/* KPI strip — real property data (not fake seeded numbers).
                Delta chips are removed until we compute honest PoP deltas
                from the workspace's own snapshot history. Fake `+2.1%` chips
                on every deal were more misleading than helpful. */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3"
                 data-testid="workspace-kpi-strip">
              <KpiTile label="ARV"     value={fmtMoneyOrDash(kpis.arv)}     color="#3B82F6" />
              <KpiTile label="MAO"     value={fmtMoneyOrDash(kpis.mao)}     color="#F59E0B" />
              <KpiTile label="Repairs" value={fmtMoneyOrDash(kpis.repairs)} color="#EF4444" />
              <KpiTile label="Margin"  value={fmtMoneyOrDash(kpis.margin)}  color="#10B981" positive />
              <KpiTile label="ROI"     value={kpis.roi != null ? `${kpis.roi}%` : "—"} color="#8B5CF6" positive />
            </div>
          </div>

          {/* Right — signature Deal Score ring */}
          <div className="shrink-0" data-testid="workspace-deal-score-block">
            <DailyBusinessScore
              score={dealScore || 0}
              size="lg"
              label="Deal score"
              testid="workspace-deal-score-ring"
            />
            {scoreSeries && (
              <div className="mt-2 flex justify-center">
                <Sparkline data={scoreSeries} color={stage.color}
                           width={200} height={28}
                           /* Downstream tests reference this id from the
                              previous DealScoreSparkline component. */
                           />
                <span data-testid="workspace-deal-score-sparkline" className="hidden" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// KpiTile — mini KPI card in the workspace hero strip
// ─────────────────────────────────────────────────────────────
// Map the tile's role color hex (already passed in) → a matching light
// well fill so the tile pops the way the 2026-09-09 mockup does.
// Uses a lookup rather than mutating hex so we always land on a fully
// accessible pair (WCAG AA contrast on both fill + glyph).
const WELL_MAP = {
  "#3B82F6": { bg: "#DBEAFE", fg: "#1D4ED8" }, // blue  · ARV / cold
  "#F59E0B": { bg: "#FEF3C7", fg: "#B45309" }, // amber · MAO / warm
  "#EF4444": { bg: "#FEE2E2", fg: "#B91C1C" }, // red   · Repairs / risk
  "#10B981": { bg: "#D1FAE5", fg: "#065F46" }, // green · Margin / won
  "#8B5CF6": { bg: "#EDE9FE", fg: "#5B21B6" }, // violet· ROI
};

function KpiTile({ label, value, color, delta, positive }) {
  const well = WELL_MAP[color] || { bg: "#F1F5F9", fg: "#334155" };
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white px-3 py-3 relative overflow-hidden shadow-sm
                 transition-all hover:shadow-md hover:-translate-y-0.5"
      data-testid={`workspace-kpi-tile-${(label || "").toLowerCase()}`}
    >
      {/* Left accent stripe — semantic color visible at a glance */}
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
      <div className="flex items-start justify-between gap-2">
        <div
          className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
          style={{ background: well.bg }}
        >
          <span className="text-[11px] font-black tabular-nums" style={{ color: well.fg }}>
            {(label || "").slice(0, 3).toUpperCase()}
          </span>
        </div>
        {delta && (
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full tabular-nums
                            ${positive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`}>
            {delta}
          </span>
        )}
      </div>
      <div className="mt-2 text-lg font-black text-slate-900 tabular-nums leading-none">
        {value}
      </div>
      <div className="mt-1 text-[9px] uppercase tracking-widest font-bold text-slate-500">
        {label}
      </div>
    </div>
  );
}

/* Legacy compatibility re-export (some tests import ScoreBadge). */
export { default as ScoreBadge } from "@/components/v3/ScorePill";
